import { AdminRole } from "@repo/api-dtos"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Plus, Shield, Trash2, UserCog } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { PageHeader, Panel } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"

export const Route = createFileRoute("/admin-users")({
	component: AdminUsersPage,
	validateSearch: (search: Record<string, unknown>): { add?: boolean } =>
		search.add === "1" || search.add === true ? { add: true } : {},
})

const ROLE_LABELS: Record<string, string> = {
	[AdminRole.Superadmin]: "Superadmin",
	[AdminRole.MarketplaceRead]: "Marketplace Read",
	[AdminRole.MarketplaceOrderSupport]: "Order Support",
	[AdminRole.MarketplaceUserManagement]: "User Management",
	[AdminRole.MarketplaceCoupons]: "Coupons",
	[AdminRole.IntegrationsAdmin]: "Integrations",
	[AdminRole.CatalogueRead]: "Catalogue Read",
	[AdminRole.CatalogueWrite]: "Catalogue Write",
	[AdminRole.CatalogueDeploy]: "Catalogue Deploy",
	[AdminRole.DiscordRead]: "Discord Read",
	[AdminRole.DiscordAdmin]: "Discord Admin",
	[AdminRole.EventsManage]: "Events",
}

const ALL_ROLES = Object.values(AdminRole)

function AdminUsersPage() {
	const { isSuperadmin } = useAdminRolesContext()
	const navigate = useNavigate()
	const { orpc, client } = useMarketplaceOrpc()
	const queryClient = useQueryClient()

	const [addDialogOpen, setAddDialogOpen] = React.useState(false)
	const [editUser, setEditUser] = React.useState<{
		userId: string
		username: string
		roles: AdminRole[]
	} | null>(null)
	const [removeUser, setRemoveUser] = React.useState<{
		userId: string
		username: string
	} | null>(null)
	const { add } = Route.useSearch()

	React.useEffect(() => {
		if (!add) return
		setAddDialogOpen(true)
		navigate({ to: "/admin-users", search: {}, replace: true })
	}, [add, navigate])

	React.useEffect(() => {
		if (!isSuperadmin) {
			navigate({ to: "/unauthorized" })
		}
	}, [isSuperadmin, navigate])

	const { data, isLoading, error, refetch } = useQuery({
		...orpc.rbac.listAdminUsers.queryOptions(),
		enabled: isSuperadmin,
	})

	const setRolesMutation = useMutation({
		mutationFn: (input: { userIdentifier: string; roles: AdminRole[] }) =>
			client.rbac.setAdminRoles(input),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: orpc.rbac.listAdminUsers.queryOptions().queryKey,
			})
			toast.success("Roles updated successfully")
			setAddDialogOpen(false)
			setEditUser(null)
		},
		onError: (error: Error) => {
			toast.error(`Failed to update roles: ${error.message}`)
		},
	})

	const removeUserMutation = useMutation({
		mutationFn: (input: { userId: string }) =>
			client.rbac.removeAdminUser(input),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: orpc.rbac.listAdminUsers.queryOptions().queryKey,
			})
			toast.success("Admin user removed successfully")
			setRemoveUser(null)
		},
		onError: (error: Error) => {
			toast.error(`Failed to remove admin: ${error.message}`)
		},
	})

	if (!isSuperadmin) return null

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Admin users"
				count={data?.users.length}
				description="Admin users and their role-based permissions"
				actions={
					<Button onClick={() => setAddDialogOpen(true)}>
						<Plus className="h-4 w-4" />
						Add admin user
					</Button>
				}
			/>

			{isLoading ? (
				<div className="flex flex-col gap-1">
					<Skeleton className="h-9 w-full" />
					<Skeleton className="h-9 w-full" />
					<Skeleton className="h-9 w-full" />
				</div>
			) : error ? (
				<div className="flex flex-col items-start gap-3 rounded-lg bg-bad-soft px-4 py-3 text-bad">
					<p>Failed to load admin users.</p>
					<Button variant="outline" size="sm" onClick={() => refetch()}>
						Retry
					</Button>
				</div>
			) : data?.users.length === 0 ? (
				<p className="text-muted-foreground">No admin users configured.</p>
			) : (
				<Panel>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>User</TableHead>
								<TableHead>Roles</TableHead>
								<TableHead>Granted by</TableHead>
								<TableHead className="text-right">Last updated</TableHead>
								<TableHead className="w-[88px]" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{data?.users.map((user) => (
								<TableRow key={user.userId}>
									<TableCell>
										<div className="flex min-w-0 items-center gap-2.5">
											{user.imageUrl ? (
												<img
													src={user.imageUrl}
													alt={user.username}
													className="size-5 rounded-full"
												/>
											) : (
												<div className="flex size-5 items-center justify-center rounded-full bg-muted">
													<Shield className="h-3 w-3" />
												</div>
											)}
											<span className="font-medium">{user.username}</span>
											<span className="truncate text-xs text-meta">
												{user.email}
											</span>
										</div>
									</TableCell>
									<TableCell>
										<div className="flex flex-wrap gap-1">
											{user.roles.map((role) => (
												<Badge
													key={role}
													variant={
														role === AdminRole.Superadmin ? "info" : "neutral"
													}
												>
													{ROLE_LABELS[role] ?? role}
												</Badge>
											))}
										</div>
									</TableCell>
									<TableCell className="text-muted-foreground">
										{user.grantedByUsername ?? "Unknown"}
									</TableCell>
									<TableCell className="text-right text-muted-foreground tabular-nums">
										{new Date(user.updatedAt).toLocaleDateString()}
									</TableCell>
									<TableCell>
										<div className="flex justify-end gap-1">
											<Button
												variant="ghost"
												size="icon"
												aria-label={`Edit roles for ${user.username}`}
												title={`Edit roles for ${user.username}`}
												onClick={() =>
													setEditUser({
														userId: user.userId,
														username: user.username,
														roles: user.roles,
													})
												}
											>
												<UserCog className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												aria-label={`Remove admin ${user.username}`}
												title={`Remove admin ${user.username}`}
												onClick={() =>
													setRemoveUser({
														userId: user.userId,
														username: user.username,
													})
												}
											>
												<Trash2 className="h-4 w-4 text-bad" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Panel>
			)}

			<AddAdminDialog
				open={addDialogOpen}
				onOpenChange={setAddDialogOpen}
				onSubmit={(userIdentifier, roles) =>
					setRolesMutation.mutate({ userIdentifier, roles })
				}
				isPending={setRolesMutation.isPending}
			/>

			<EditRolesDialog
				user={editUser}
				onOpenChange={(open) => !open && setEditUser(null)}
				onSubmit={(roles) =>
					editUser &&
					setRolesMutation.mutate({ userIdentifier: editUser.userId, roles })
				}
				isPending={setRolesMutation.isPending}
			/>

			<RemoveAdminDialog
				user={removeUser}
				onOpenChange={(open) => !open && setRemoveUser(null)}
				onConfirm={() =>
					removeUser && removeUserMutation.mutate({ userId: removeUser.userId })
				}
				isPending={removeUserMutation.isPending}
			/>
		</div>
	)
}

function AddAdminDialog({
	open,
	onOpenChange,
	onSubmit,
	isPending,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onSubmit: (userIdentifier: string, roles: AdminRole[]) => void
	isPending: boolean
}) {
	const [userIdentifier, setUserIdentifier] = React.useState("")
	const [selectedRoles, setSelectedRoles] = React.useState<AdminRole[]>([])

	React.useEffect(() => {
		if (!open) {
			setUserIdentifier("")
			setSelectedRoles([])
		}
	}, [open])

	const toggleRole = (role: AdminRole) => {
		setSelectedRoles((prev) =>
			prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
		)
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Add admin user</DialogTitle>
					<DialogDescription>
						Enter the user's username, email, or internal ID and select their
						roles.
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<Label htmlFor="userIdentifier">Username, Email, or User ID</Label>
						<Input
							id="userIdentifier"
							placeholder="e.g. johndoe or john@example.com"
							value={userIdentifier}
							onChange={(e) => setUserIdentifier(e.target.value)}
						/>
					</div>
					<div className="space-y-2">
						<Label>Roles</Label>
						<div className="grid grid-cols-2 gap-2">
							{ALL_ROLES.map((role) => (
								<label
									key={role}
									htmlFor={`role-${role}`}
									className="flex items-center gap-2 rounded-md border p-2 cursor-pointer hover:bg-accent"
								>
									<Checkbox
										id={`role-${role}`}
										checked={selectedRoles.includes(role)}
										onCheckedChange={() => toggleRole(role)}
									/>
									<span className="text-sm">{ROLE_LABELS[role] ?? role}</span>
								</label>
							))}
						</div>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button
						onClick={() => onSubmit(userIdentifier.trim(), selectedRoles)}
						disabled={
							!userIdentifier.trim() || selectedRoles.length === 0 || isPending
						}
					>
						{isPending ? "Adding..." : "Add Admin"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function EditRolesDialog({
	user,
	onOpenChange,
	onSubmit,
	isPending,
}: {
	user: { userId: string; username: string; roles: AdminRole[] } | null
	onOpenChange: (open: boolean) => void
	onSubmit: (roles: AdminRole[]) => void
	isPending: boolean
}) {
	const [selectedRoles, setSelectedRoles] = React.useState<AdminRole[]>([])

	React.useEffect(() => {
		if (user) {
			setSelectedRoles([...user.roles])
		}
	}, [user])

	const toggleRole = (role: AdminRole) => {
		setSelectedRoles((prev) =>
			prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
		)
	}

	return (
		<Dialog open={!!user} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Edit roles for {user?.username}</DialogTitle>
					<DialogDescription>
						Update the roles assigned to this admin user.
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<Label>Roles</Label>
						<div className="grid grid-cols-2 gap-2">
							{ALL_ROLES.map((role) => (
								<label
									key={role}
									htmlFor={`edit-role-${role}`}
									className="flex items-center gap-2 rounded-md border p-2 cursor-pointer hover:bg-accent"
								>
									<Checkbox
										id={`edit-role-${role}`}
										checked={selectedRoles.includes(role)}
										onCheckedChange={() => toggleRole(role)}
									/>
									<span className="text-sm">{ROLE_LABELS[role] ?? role}</span>
								</label>
							))}
						</div>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button
						onClick={() => onSubmit(selectedRoles)}
						disabled={selectedRoles.length === 0 || isPending}
					>
						{isPending ? "Saving..." : "Save Roles"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function RemoveAdminDialog({
	user,
	onOpenChange,
	onConfirm,
	isPending,
}: {
	user: { userId: string; username: string } | null
	onOpenChange: (open: boolean) => void
	onConfirm: () => void
	isPending: boolean
}) {
	return (
		<Dialog open={!!user} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Remove admin user</DialogTitle>
					<DialogDescription>
						Are you sure you want to remove <strong>{user?.username}</strong> as
						an admin? This will revoke all their admin roles and Clerk admin
						access.
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button
						variant="destructive"
						onClick={onConfirm}
						disabled={isPending}
					>
						{isPending ? "Removing..." : "Remove Admin"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
